const defaultTheme = require('tailwindcss/defaultTheme');
module.exports = {
  purge: {
    content: ['./index.html', './src/**/*.{vue,js,ts,jsx,tsx}'],
    options: {
      safelist: [/^shadow/, /^bg/, /^text/, /^border/, /^from/, /^to/, /^dark:/, /^hover:/, 'dark', /^dark:/]
    }
  },
  darkMode: 'class', // false or 'media' or 'class'
  theme: {
    fontFamily: {
      body: [
        'Graphik',
        'Inter-Variable',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Helvetica',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"'
      ],
      display: ['"Graphik"', 'Times', '"Times New Roman"', 'serif']
    },
    boxShadow: {
      sm: '0 2px 4px 0 rgba(0,0,0,0.05)',
      DEFAULT: '0px 4px 6px -1px rgba(0, 0, 0, 0.05), 0px 2px 4px -1px rgba(0, 0, 0, 0.05)',
      lg: '0 2px 4px 0 rgba(0,0,0,0.03), 0 10px 40px 0 rgba(0,0,0,0.05)',
      xl: '0 2px 4px 0 rgba(0,0,0,0.05), 0 0px 40px 0 rgba(0,0,0,0.1)',
      inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
      none: 'none'
    },
    borderColor: theme => ({
      ...theme('colors'),
      DEFAULT: theme('colors.gray.100', 'currentColor')
    }),
    screens: {
      xs: '440px',
      ...defaultTheme.screens
    },
    extend: {
      height: {
        '112': '28rem'
      },
      colors: {
        // Vibrant nature-inspired greens, ranging from pure green to lime shades
        // Useful for success states, environmental themes, and organic elements
        green: {
          50: '#00FF00',    // Pure green - maximum brightness
          100: '#FAFC00',   // Bright yellow-green
          200: '#D0FC00',   // Lime yellow
          300: '#A6FC00',   // Fresh lime
          400: '#00FC02',   // Bright digital green
          500: '#28FC00',   // Electric lime
          600: '#52FC00',   // Medium lime
          700: '#7CFC00',   // Lawn green
          800: '#69D600',   // Muted lime
          850: '#56B000',   // Forest green
          900: '#448900',   // Dark forest green
          943: '#05D690'    // Turquoise accent
        },

        // Neutral gray scale with purple tints at the end
        // Perfect for text, backgrounds, and UI elements
        gray: {
          50: '#f9f9f9',    // Nearly white
          100: '#999999',   // Light gray
          200: '#888888',   // Medium-light gray
          300: '#777777',   // Medium gray
          400: '#666666',   // Medium-dark gray
          500: '#555555',   // Dark gray
          600: '#444444',   // Darker gray
          700: '#333333',   // Very dark gray
          800: '#111d19',   // Nearly black with slight green tint
          850: '#000000',   // Pure black
          900: '#000000',   // Pure black
          950: '#C3FFD1',   // Light green-gray
          1050: '#C1D1C1'   // Soft green-gray
        },

        // Dark mode focused colors, perfect for dark themes and backgrounds
        // Creates depth and hierarchy in dark interfaces
        primary: {
          50: '#53555e',    // Light slate gray
          100: '#494b54',   // Slate gray
          200: '#3f414a',   // Medium slate
          300: '#353740',   // Dark slate
          400: '#2b2d36',   // Deeper slate
          500: '#21232c',   // Very dark slate
          600: '#171922',   // Almost black slate
          700: '#0d0f18',   // Night black
          800: '#03050e',   // Deep night
          900: '#000004'    // Void black
        },

        // Bright accent colors with blue base
        // Good for primary actions and highlights
        'primary-dark': {
          50: '#6a7cff',    // Light periwinkle
          100: '#6072ff',   // Bright periwinkle
          200: '#5668ff',   // Electric blue
          300: '#4c5eff',   // Royal blue
          400: '#4254ff',   // Deep royal blue
          500: '#05D690',   // Turquoise accent
          600: '#2e40f5',   // Bright navy
          700: '#2436eb',   // Deep navy
          800: '#1a2ce1',   // Dark navy
          900: '#1022d7'    // Midnight blue
        },

        // Rich blue spectrum from bright to deep navy
        // Perfect for links, buttons, and interactive elements
        blue: {
          50: '#7affa6',    // Light mint
          100: '#66f596',   // Bright mint
          200: '#52eb86',   // Electric mint
          300: '#3ee176',   // Fresh mint
          400: '#2ad766',   // Deep mint
          500: '#16cd56',   // Intense mint
          600: '#02c346',   // Bright forest
          700: '#00b940',   // Deep forest
          800: '#00af3a',   // Dark forest
          900: '#00a534',   // Deep green
          1000: '#31920f',  // Forest green (unchanged)
          1100: '#0e700e'   // Dark forest (unchanged)
        },

        // Vibrant pink spectrum for high-energy accents
        // Great for highlighting and special elements
        pink: {
          50: '#4dff9f',    // Bright mint
          100: '#43ff97',    // Electric mint
          200: '#39ff8f',    // Hot mint
          300: '#2fff87',    // Neon mint
          400: '#25ff7f',    // Vibrant mint
          500: '#1bf677',    // Deep mint
          600: '#11ec6f',    // Rich mint
          700: '#07e267',    // Dark mint
          800: '#00d85f',    // Deep green
          900: '#00ce57',    // Forest green
          950: '#00b84f',    // Dark forest green
          1000: '#009f45'    // Deep forest green
        },

        // Fresh green palette (despite name 'gold')
        // Suitable for nature-themed elements and success states
        gold: {
          50: '#7AFFA6',    // Bright mint
          100: '#4DFF89',   // Fresh mint
          200: '#1FE065',   // Medium mint
          300: '#00C649',   // Forest mint
          400: '#58FF8E',   // Spring green
          500: '#05D690',   // Turquoise accent
          600: '#00B649',   // Deep mint
          700: '#007340',   // Forest green
          800: '#174A25',   // Dark forest
          900: '#072E1E'    // Deep forest
        },

        // Minimal red palette for warnings and errors
        red: {
          500: '#05D690',   // Turquoise accent
          600: '#ff2500'    // Bright warning red
        },

        // Single purple shade for specific accents
        purple: {
          50: '#2E924F',    
          500: '#00FF00',    // Pure green - maximum brightness
          // Royal green
        }
      }
    }
  },
  variants: {
    extend: {
      margin: ['first'],
      borderRadius: ['first', 'last'],
      zIndex: ['hover'],
      borderWidth: ['dark', 'last']
    }
  },
  plugins: []
};
